<template>
    <!-- 데이터가 없거나 로딩 중일 때는 컴포넌트를 렌더링하지 않음 -->
    <div v-if="hasValidData" class="eligibility-tracking-card">
        <!-- 헤더 -->
        <div class="eligibility-tracking-head">
            <div class="tracking-head-title h4">
                <div class="loading-text" :data-text="Title">
                    {{ Title }}
                </div>
                <div class="tracking-head-sub caption--regular">{{ Body }}</div>
            </div>
        </div>
        <!-- 본문 -->
        <ul class="eligibility-tracking-list">
            <li class="tracking-list-item" v-for="(item, index) in WaitingPublishSentenceList" :key="index">
                <div class="item-title-wrap">
                    <div class="item-title caption--regular" :style="{ color: item.GateWayTextColor ?? '' }">
                        {{ item.GateWayText }}
                    </div>
                    <div class="item-dash"></div>
                </div>
                <div
                    class="item-status"
                    :style="{
                        color: item.StatusTextColor ?? '',
                        fontWeight: item.IsStatusTextBold ? 'bold' : 'normal',
                    }"
                >
                    {{ item.StatusText }}
                </div>
            </li>
        </ul>
        <CommBRButton
            v-if="ReportPublishButton"
            :text="ReportPublishButton.Text ?? ''"
            :bgColor="ReportPublishButton.BackgroundColor ?? ''"
            :textColor="ReportPublishButton.TextColor ?? ''"
            fontSize="1.6rem"
            padding="1.5rem"
            borderRadius="1.5rem"
            @clickButton="handleClickReport"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { WaitingPublishModuleSchema } from '@common/openapi-client';
import CommBRButton from '@common/common/components/CommBRButton.vue';
import useProReportStore from '@common/trend-report-common/src/stores/proReport';

const emit = defineEmits(['clickPublish']);

const proReportStore = useProReportStore();

const props = withDefaults(
    defineProps<{
        cardData?: WaitingPublishModuleSchema;
    }>(),
    {
        cardData: undefined,
    },
);

// toRefs 대신 computed를 사용하여 반응성 보장
const Title = computed(() => props.cardData?.Title);
const Body = computed(() => props.cardData?.Body);
const WaitingPublishSentenceList = computed(() => props.cardData?.WaitingPublishSentenceList || []);
const ReportPublishButton = computed(() => props.cardData?.ReportPublishButton);

// 데이터가 유효한지 확인하는 computed
const hasValidData = computed(() => {
    return (
        props.cardData &&
        (props.cardData.Title ||
            props.cardData.Body ||
            (props.cardData.WaitingPublishSentenceList && props.cardData.WaitingPublishSentenceList.length > 0) ||
            props.cardData.ReportPublishButton)
    );
});

const handleClickReport = () => {
    const generationDateTime = props.cardData?.ReportPublishButton?.GenerationDateTime ?? '';
    const reportType = props.cardData?.ReportPublishButton?.ReportType ?? '';

    proReportStore.postClickReportPublishButton(generationDateTime, reportType).then(res => {
        if (res) {
            emit('clickPublish', generationDateTime, reportType);
        }
    });
};
</script>

<style scoped lang="scss">
.eligibility-tracking-card {
    position: fixed;
    width: 100%;
    box-sizing: border-box;
    bottom: 0;
    z-index: 10;
    background: $grey-9;
    color: $primary-white;
    border-radius: 4.5rem 4.5rem 0 0;
    padding: 4.5rem 2rem;
}

.eligibility-tracking-head {
    display: flex;
    align-items: flex-end;
    gap: 1.2rem;
    padding-bottom: 2rem;

    .tracking-head-title {
        position: relative;
        display: inline-block;
        overflow: hidden;
    }

    .tracking-head-sub {
        color: $pro-secondary-blue;
    }

    .loading-text {
        font-size: 20px;
        font-weight: bold;
        color: $primary-white;
        line-height: 150%;
        display: inline-block;
        position: relative;
        overflow: hidden;
        border-radius: 0.5rem;

        &::before {
            content: attr(data-text);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(
                90deg,
                #fff 0%,
                #fff 15%,
                #e8ecf2 25%,
                #8d9cb3 35%,
                #8d9cb3 65%,
                #e8ecf2 75%,
                #fff 85%,
                #fff 100%
            );
            background-size: 200% 100%;
            animation: loadingAnimation 3s infinite linear;
            background-clip: text;
            -webkit-background-clip: text;
            color: transparent;
            /* 성능 최적화: GPU 가속 활성화 */
            transform: translateZ(0);
            backface-visibility: hidden;
        }
    }

    @keyframes loadingAnimation {
        0% {
            background-position: 200% 0;
        }

        100% {
            background-position: -200% 0;
        }
    }
}

.eligibility-tracking-list {
    padding-bottom: 2rem;
    li:last-child {
        padding-bottom: 0;
    }
}

.tracking-list-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.2rem;
    padding-bottom: 0.7rem;

    .item-title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
        width: calc(100% - 4.8rem);
    }

    .item-title {
        color: $grey-6;
    }

    .item-dash {
        flex: 1;
        height: 1px;
        background-image: repeating-linear-gradient(to right, $grey-7 0, $grey-7 2px, transparent 2px, transparent 4px);
    }

    .item-status {
        width: 4.8rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.2rem;
    }
}
</style>
